import dynamic from 'next/dynamic';
import { ComponentConfig } from '@zapier/builder-io-kit-compat';
import type { ButtonProps } from '@zapier/design-system';

/** The legacy `color` input, which design system v10 renamed to `variant`. */
type LegacyProps = { color?: ButtonProps['variant'] };

/**
 * Design system v10 renamed this prop from `color` to `variant`. Builder stores
 * the input name alongside the content, so sections saved before the rename
 * still arrive as `color` — map it across instead of letting those buttons
 * silently fall back to the default variant until someone re-saves them in
 * Builder.io. `variant` wins when both are present.
 */
export function withLegacyColorProp(
  Button: React.ComponentType<ButtonProps>
): React.ComponentType<ButtonProps & LegacyProps> {
  return function BuilderButton({ color, variant, ...rest }) {
    return <Button variant={variant ?? color} {...rest} />;
  };
}

export const config: ComponentConfig = {
  name: 'Button',
  description:
    'Classic button component. Use when providing calls to action to users.',
  category: 'Zapier Sign Up Blocks',
  // @ts-ignore
  component: dynamic(async () => {
    const { Button } = await import('@zapier/design-system');

    return withLegacyColorProp(Button);
  }),
  inputs: [
    {
      name: 'children',
      friendlyName: 'Label',
      type: 'string',
      defaultValue: 'Go',
      helperText: 'Text that describes the action the button will take.',
    },
    {
      name: 'variant',
      friendlyName: 'Variant',
      type: 'string',
      defaultValue: 'primary',
      enum: [
        { label: 'Primary', value: 'primary' },
        { label: 'Secondary', value: 'secondary' },
      ],
      helperText: 'The visual style to render for Button',
    },
  ],
};
